'use client';

import { HelpCircle } from 'lucide-react';
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

type InfoTooltipProps = {
  content: ReactNode;
  label?: string;
  className?: string;
  maxWidth?: string;
};

export function InfoTooltip({ content, label, className, maxWidth = '300px' }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);

  return (
    <TooltipProvider>
      <Tooltip delayDuration={100} open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          {label ? (
            <button
              ref={trigger}
              type="button"
              aria-label={label}
              onPointerDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.preventDefault();
                setOpen((current) => !current);
              }}
              className={cn(
                'relative inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-zinc-500 hover:text-zinc-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 before:absolute before:-inset-1 [@media(pointer:coarse)]:before:-inset-3.5',
                className,
              )}
            >
              <HelpCircle aria-hidden="true" className="size-4" />
            </button>
          ) : (
            <HelpCircle
              className={cn(
                'size-4 text-zinc-500 cursor-help hover:text-zinc-400 transition-colors',
                className,
              )}
            />
          )}
        </TooltipTrigger>
        <TooltipContent
          onPointerDownOutside={(event) => {
            if (trigger.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}
          className="whitespace-normal text-sm tracking-wide font-normal"
          style={{ maxWidth }}
          sideOffset={6}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
